// Modal shown when switching a tour from concept to published mode
// Displays grouped validation errors per step with navigation links

import React from 'react';
import { X, AlertTriangle, ArrowRight, CheckCircle2 } from 'lucide-react';
import { PublishValidationGroup } from '../../utils/tourValidation';

interface PublishValidationModalProps {
  groups: PublishValidationGroup[];
  onClose: () => void;
  onGoToStep: (step: number) => void;
  onConfirm: () => void;
}

const PublishValidationModal: React.FC<PublishValidationModalProps> = ({
  groups,
  onClose,
  onGoToStep,
  onConfirm,
}) => {
  const hasErrors = groups.length > 0;
  const totalErrors = groups.reduce((sum, g) => sum + g.errors.length, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#1a2e1a] border border-white/10 rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <div className={`px-6 py-5 border-b border-white/10 ${
          hasErrors ? 'bg-red-500/5' : 'bg-emerald-500/5'
        }`}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              {hasErrors ? (
                <div className="p-2 rounded-full bg-red-500/15">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
              ) : (
                <div className="p-2 rounded-full bg-emerald-500/15">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                </div>
              )}
              <div>
                <h3 className="text-lg font-semibold text-white">
                  {hasErrors ? 'Not Ready to Publish' : 'Ready to Publish'}
                </h3>
                <p className="text-sm text-white/50 mt-0.5">
                  {hasErrors
                    ? `${totalErrors} issue${totalErrors > 1 ? 's' : ''} must be resolved before publishing`
                    : 'All required fields are complete. You can publish this tour.'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-white/10 text-white/40 hover:text-white transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {hasErrors ? (
            groups.map((group) => (
              <div key={group.step} className="bg-white/5 rounded-lg border border-white/10 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500/20 text-red-400 text-[10px] font-bold">
                      {group.errors.length}
                    </span>
                    <span className="text-sm font-medium text-white">{group.label}</span>
                  </div>
                  <button
                    onClick={() => {
                      onGoToStep(group.step);
                      onClose();
                    }}
                    className="flex items-center gap-1 text-xs text-[#E85C0D] hover:text-[#ff6a0d] transition-colors font-medium"
                  >
                    Go to step
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
                <ul className="px-4 py-2.5 space-y-1.5">
                  {group.errors.map((err, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/60">
                      <span className="w-1 h-1 rounded-full bg-red-400/60 mt-2 shrink-0" />
                      {err}
                    </li>
                  ))}
                </ul>
              </div>
            ))
          ) : (
            <div className="text-center py-6">
              <CheckCircle2 className="w-12 h-12 text-emerald-400/40 mx-auto mb-3" />
              <p className="text-white/60 text-sm">
                All required information has been filled in. Switching to published mode will make this tour visible to the public once saved.
              </p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-white/10 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all"
          >
            {hasErrors ? 'Stay as Concept' : 'Cancel'}
          </button>
          {hasErrors ? (
            <button
              onClick={() => {
                onGoToStep(groups[0].step);
                onClose();
              }}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#E85C0D] text-white rounded-lg hover:bg-[#ff6a0d] transition-all text-sm font-medium"
            >
              Fix First Issue
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={onConfirm}
              className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all text-sm font-medium"
            >
              Switch to Published
              <CheckCircle2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublishValidationModal;
